import type { Transaction } from 'src/apis/capacity-management/types';
import type { MyTransactionsRequest } from 'src/apis/capacity-management/use-my-transactions';
import { useGetSurrenderActions } from 'src/apis/surrender-service-legacy/use-get-surrender-actions';
import type { LegacySurrenderability } from 'src/apis/surrender-service-legacy/utils';
import { getLegacyCreateSurrenderPath } from 'src/apis/surrender-service-legacy/utils';
import { useOptionalDealsSurrenderability } from 'src/apis/surrender-service/use-deals-surrenderability';
import type { OptionalSurrenderRootRequest } from 'src/apis/surrender-service/use-surrender-root';
import { getCreateSurrenderPath } from 'src/apis/surrender-service/utils';

export type GetCreateSurrenderPath = (
  transaction: Transaction
) => string | null;

/**
 * Resolves the path to the "Create Surrender Request" wizard of each transaction in
 * `myTransactions`, `null` when the backend offers none. With the `getDealsSurrenderability` root
 * link for CaMa (TAG) and non-CaMa transactions, otherwise via the legacy CaMa flow. Until the
 * backend answers, no path is offered.
 */
export function useCreateSurrenderPaths(
  myTransactions: MyTransactionsRequest,
  surrenderRoot: OptionalSurrenderRootRequest
): GetCreateSurrenderPath {
  const hasSurrenderability = Boolean(
    surrenderRoot.response?.data._links.getDealsSurrenderability
  );

  const surrenderability = useOptionalDealsSurrenderability(
    myTransactions,
    surrenderRoot
  );
  const { deals, trades } = useGetSurrenderActions(
    myTransactions,
    surrenderRoot,
    !hasSurrenderability
  );

  if (hasSurrenderability) {
    const dealSurrenderabilities =
      surrenderability?.response?.data._embedded.items ?? [];

    return (transaction) =>
      getCreateSurrenderPath(transaction, dealSurrenderabilities);
  }

  const legacySurrenderability: LegacySurrenderability = {
    deals: deals.response?.data._embedded.items ?? [],
    trades: trades.response?.data._embedded.items ?? [],
  };

  return (transaction) =>
    getLegacyCreateSurrenderPath(transaction, legacySurrenderability);
}
